// createLazyIcon.ts
import { lazy, Suspense } from "react";
import type { IconBaseProps, IconType } from "react-icons";

export function createLazyIcon(importer: () => Promise<Record<string, IconType>>, name: string) {
	const LazyIcon = lazy(async () => {
		const mod = await importer();
		return { default: mod[name] };
	});

	return function WrappedIcon(props: IconBaseProps) {
		return (
			<Suspense fallback={null}>
				<LazyIcon {...props} />
			</Suspense>
		);
	};
}

export const icons: Record<string, React.ComponentType<IconBaseProps>> = {
	BsCart3: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsCart3"),
	BsFileEarmarkPdfFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsFileEarmarkPdfFill"),
	BsFillImageFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsFillImageFill"),
	BsPrinterFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsPrinterFill"),
	BsArrowRightCircleFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowRightCircleFill"),
	BsExclamationDiamond: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsExclamationDiamond"),
	BsTrashFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsTrashFill"),
	BsArrowLeftCircleFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowLeftCircleFill"),
	BsArrowBarDown: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowBarDown"),
	BsArrowBarUp: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowBarUp"),
	BsArrowDownShort: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowDownShort"),
	BsArrowLeftCircle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowLeftCircle"),
	BsArrowRightCircle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowRightCircle"),
	BsArrowUpShort: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowUpShort"),
	BsFullscreen: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsFullscreen"),
	BsShuffle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsShuffle"),
	BsPersonFillSlash: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsPersonFillSlash"),
	BsPersonXFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsPersonXFill"),
	BsArrowRepeat: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowRepeat"),
	BsCheckCircle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsCheckCircle"),
	BsQuestionCircle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsQuestionCircle"),
	BsXCircle: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsXCircle"),
	BsArrowUp: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsArrowUp"),
	BsPersonPlusFill: createLazyIcon(() => import("react-icons/bs") as Promise<Record<string, any>>, "BsPersonPlusFill"),
};

type IconName = keyof typeof icons;
export function Icon({ name, size = 20, className, color }: { name: IconName; size?: number | string, className? : string, color? : string }) {
	const Icon = icons[name];
	return <Icon size={size} className={className} color={color}/>;
}
